import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  Calendar, 
  User, 
  CheckCircle, 
  XCircle,
  Clock,
  Eye,
  Trash2,
  Search,
  Filter,
  Download,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const PaymentProofList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProof, setSelectedProof] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchPaymentProofs();
  }, []);

  const fetchPaymentProofs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ngoToken');
      
      if (!token) {
        toast.error('Authentication required');
        navigate('/ngo/login');
        return;
      }
      
      // Try multiple possible endpoints to handle different server configurations
      const possibleEndpoints = [
        'http://localhost:3011/api/payment-proofs/ngo',
        'http://localhost:5000/api/payment-proofs/ngo',
        'https://bloodbackend-99n2.onrender.com/api/payment-proofs/ngo'
      ];
      
      let response = null;
      let error = null;
      
      // Try each endpoint until one works
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`Trying to fetch payment proofs from endpoint: ${endpoint}`);
          
          response = await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            timeout: 5000 // 5 second timeout
          });
          
          // If we get here, the request was successful
          console.log('Payment proofs fetched successfully:', response.data);
          break;
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed:`, err.message);
          error = err;
          // Continue to next endpoint
        }
      }
      
      if (response && response.data && response.data.success) {
        setPaymentProofs(response.data.data);
      } else {
        toast.error('Failed to load payment proofs');
      }
    } catch (error) {
      console.error('Error fetching payment proofs:', error);
      toast.error('Failed to load payment proofs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (proofId, newStatus) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ngoToken');
      
      if (!token) {
        toast.error('Authentication required');
        navigate('/ngo/login');
        return;
      }
      
      // Try multiple possible endpoints to handle different server configurations
      const possibleEndpoints = [
        `http://localhost:3011/api/payment-proofs/${proofId}`,
        `http://localhost:5000/api/payment-proofs/${proofId}`,
        `https://bloodbackend-99n2.onrender.com/api/payment-proofs/${proofId}`
      ];
      
      let response = null;
      let error = null;
      
      // Try each endpoint until one works
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`Trying to update payment proof status at endpoint: ${endpoint}`);
          
          response = await axios.put(endpoint, 
            { status: newStatus },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 5000 // 5 second timeout
            }
          );
          
          // If we get here, the request was successful
          console.log('Payment proof status updated successfully:', response.data);
          break;
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed:`, err.message);
          error = err;
          // Continue to next endpoint
        }
      }
      
      if (response && response.data && response.data.success) {
        toast.success(`Payment proof ${newStatus.toLowerCase()} successfully`);
        
        // Update the local state
        setPaymentProofs(prevProofs => 
          prevProofs.map(proof => 
            proof._id === proofId ? { ...proof, status: newStatus } : proof
          )
        );
        
        // Close modal if open
        if (isModalOpen) {
          setIsModalOpen(false);
          setSelectedProof(null);
        }
      } else {
        toast.error(`Failed to ${newStatus.toLowerCase()} payment proof`);
      }
    } catch (error) {
      console.error('Error updating payment proof status:', error);
      toast.error(`Failed to ${newStatus.toLowerCase()} payment proof`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProof = async (proofId) => {
    if (window.confirm('Are you sure you want to delete this payment proof? This action cannot be undone.')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('ngoToken');
        
        if (!token) {
          toast.error('Authentication required');
          navigate('/ngo/login');
          return;
        }
        
        // Try multiple possible endpoints to handle different server configurations
        const possibleEndpoints = [
          `http://localhost:3011/api/payment-proofs/${proofId}`,
          `http://localhost:5000/api/payment-proofs/${proofId}`,
          `https://bloodbackend-99n2.onrender.com/api/payment-proofs/${proofId}`
        ];
        
        let response = null;
        let error = null;
        
        // Try each endpoint until one works
        for (const endpoint of possibleEndpoints) {
          try {
            console.log(`Trying to delete payment proof at endpoint: ${endpoint}`);
            
            response = await axios.delete(endpoint, {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 5000 // 5 second timeout
            });
            
            // If we get here, the request was successful
            console.log('Payment proof deleted successfully');
            break;
          } catch (err) {
            console.log(`Endpoint ${endpoint} failed:`, err.message);
            error = err;
            // Continue to next endpoint
          }
        }
        
        if (response && response.data && response.data.success) {
          toast.success('Payment proof deleted successfully');
          
          // Update the local state
          setPaymentProofs(prevProofs => 
            prevProofs.filter(proof => proof._id !== proofId)
          );
          
          // Close modal if open
          if (isModalOpen) {
            setIsModalOpen(false);
            setSelectedProof(null);
          }
        } else {
          toast.error('Failed to delete payment proof');
        }
      } catch (error) {
        console.error('Error deleting payment proof:', error);
        toast.error('Failed to delete payment proof');
      } finally {
        setLoading(false);
      }
    }
  };

  // Filter payment proofs based on status and search term
  const filteredProofs = paymentProofs.filter(proof => {
    const matchesFilter = filter === 'all' || proof.status.toLowerCase() === filter.toLowerCase();
    const matchesSearch = 
      proof.donor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proof.donor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (proof.project && proof.project.title && proof.project.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (proof.transactionId && proof.transactionId.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time
  const formatTime = (dateString) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'verified':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'rejected':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Payment Proofs
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Manage and verify payment proofs submitted by donors
        </p>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-2">
          <Filter size={16} className="text-gray-500" />
          <button 
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-xs font-medium rounded-full ${filter === 'all' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
          >
            All
          </button>
          <button 
            onClick={() => setFilter('pending')}
            className={`px-3 py-1 text-xs font-medium rounded-full ${filter === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
          >
            Pending
          </button>
          <button 
            onClick={() => setFilter('verified')}
            className={`px-3 py-1 text-xs font-medium rounded-full ${filter === 'verified' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
          >
            Verified
          </button>
          <button 
            onClick={() => setFilter('rejected')}
            className={`px-3 py-1 text-xs font-medium rounded-full ${filter === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
          >
            Rejected
          </button>
        </div>
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Search payments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full py-2 pl-10 pr-4 text-gray-700 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
        </div>
      </div>

      {/* Payment Proofs List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredProofs.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm || filter !== 'all' 
              ? 'No payment proofs match your search or filter criteria.' 
              : 'No payment proofs have been submitted yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Donor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Project
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProofs.map((proof) => (
                  <tr key={proof._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {proof.donor.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {proof.donor.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {proof.project && proof.project.title ? proof.project.title : 'Unknown Project'}
                      </div>
                      {proof.transactionId && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Tx: {proof.transactionId}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                        <DollarSign size={16} className="mr-1 text-green-500" />
                        ₹{proof.amount.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {proof.paymentMethod}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDate(proof.paymentDate)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(proof.paymentDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(proof.status)}`}>
                        {getStatusIcon(proof.status)}
                        <span className="ml-1">{proof.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedProof(proof);
                            setIsModalOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        {proof.status === 'Pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateStatus(proof._id, 'Verified')}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              title="Verify Payment"
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(proof._id, 'Rejected')}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Reject Payment"
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteProof(proof._id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete Payment Proof"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Proof Detail Modal */}
      {isModalOpen && selectedProof && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Payment Proof Details
                    </h3>
                    <div className="mt-4">
                      {/* Payment Screenshot */}
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Payment Screenshot
                        </h4>
                        <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-md">
                          <img 
                            src={`${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/payment-proofs/image/${selectedProof._id}`} 
                            alt="Payment Screenshot" 
                            className="w-full h-auto rounded-md"
                            onError={(e) => {
                              console.error('Error loading image:', e);
                              if (e.target) {
                                console.log('Attempted URL:', e.target.src);
                                e.target.src = 'https://via.placeholder.com/400x300?text=Payment+Screenshot+Not+Available';
                                e.target.alt = 'Payment Screenshot Not Available';
                              }
                            }}
                          />
                          <div className="mt-2 flex justify-between">
                            <span className="text-xs text-gray-500">{selectedProof.paymentMethod} Payment</span>
                            <a 
                              href={`${process.env.REACT_APP_API_URL || 'http://localhost:3011'}/payment-proofs/image/${selectedProof._id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              onClick={(e) => {
                                // Log the URL for debugging
                                console.log('Opening screenshot URL:', e.target.href);
                              }}
                            >
                              <ExternalLink size={12} className="mr-1" />
                              Open Original
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Payment Details */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Donor
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedProof.donor.name}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Email
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedProof.donor.email}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Amount
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            ₹{selectedProof.amount.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Payment Method
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedProof.paymentMethod}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Date
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedProof.paymentDate)} {formatTime(selectedProof.paymentDate)}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Status
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(selectedProof.status)}`}>
                            {getStatusIcon(selectedProof.status)}
                            <span className="ml-1">{selectedProof.status}</span>
                          </span>
                        </div>
                      </div>

                      {/* Transaction ID */}
                      {selectedProof.transactionId && (
                        <div className="mb-4">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Transaction ID
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedProof.transactionId}
                          </p>
                        </div>
                      )}

                      {/* Notes */}
                      {selectedProof.notes && (
                        <div className="mb-4">
                          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Notes
                          </h4>
                          <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                            {selectedProof.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedProof.status === 'Pending' && (
                  <>
                    <button 
                      type="button" 
                      onClick={() => handleUpdateStatus(selectedProof._id, 'Verified')}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      <CheckCircle size={16} className="mr-2" />
                      Verify
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleUpdateStatus(selectedProof._id, 'Rejected')}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      <XCircle size={16} className="mr-2" />
                      Reject
                    </button>
                  </>
                )}
                <button 
                  type="button" 
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedProof(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentProofList;
